import React, { useMemo, useState } from 'react';
import { ChevronDown, Leaf, Search } from 'lucide-react';
import { ClothingBrowse } from '../../../hooks/useClothingBrowse';
import { FacetRow, cleanBrand } from '../../../lib/clothingBrowseApi';
import {
  ADULT_SIZES,
  COLOUR_FAMILIES,
  PRICE_PRESETS,
  TYPE_GROUPS,
  TypeGroup,
} from '../../../data/clothing/browseConfig';
import ClothingSmartSearch from './ClothingSmartSearch';

const GREEN = '#78BE20';

const countOf = (rows: FacetRow[] | undefined, value: string): number | undefined =>
  rows ? rows.find((r) => r.value === value)?.count ?? 0 : undefined;

const Count: React.FC<{ n?: number }> = ({ n }) =>
  n === undefined ? null : <span className="ml-auto font-poppins text-[11px] tabular-nums text-black/40">{n.toLocaleString('en-GB')}</span>;

const Section: React.FC<{ title: string; badge?: number; defaultOpen?: boolean; children: React.ReactNode }> = ({
  title, badge, defaultOpen = true, children,
}) => (
  <details open={defaultOpen} className="group border-b border-black/10 py-4">
    <summary className="flex items-center cursor-pointer list-none select-none [&::-webkit-details-marker]:hidden">
      <span className="font-montserrat font-extrabold text-[13px] tracking-[0.08em] uppercase">{title}</span>
      {badge ? (
        <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: GREEN }}>
          {badge}
        </span>
      ) : null}
      <ChevronDown className="ml-auto w-4 h-4 text-black/50 transition-transform duration-200 group-open:rotate-180" />
    </summary>
    <div className="pt-3">{children}</div>
  </details>
);

const Check: React.FC<{ label: string; checked: boolean; onChange: () => void; count?: number; disabled?: boolean }> = ({
  label, checked, onChange, count, disabled,
}) => (
  <label className={`flex items-center gap-2.5 py-1.5 cursor-pointer font-poppins text-[13px] ${disabled ? 'opacity-40' : ''}`}>
    <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled && !checked} className="peer sr-only" />
    <span
      className="w-4 h-4 rounded-[4px] border border-black/30 flex items-center justify-center shrink-0 transition-colors peer-checked:border-[#78BE20] peer-checked:bg-[#78BE20] peer-focus-visible:ring-2 peer-focus-visible:ring-[#78BE20]/40"
      aria-hidden
    >
      {checked && <svg viewBox="0 0 12 12" className="w-3 h-3 text-white"><path d="M2 6l3 3 5-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
    </span>
    <span className="truncate">{label}</span>
    <Count n={count} />
  </label>
);

const Pill: React.FC<{ label: string; active: boolean; onClick: () => void; disabled?: boolean }> = ({ label, active, onClick, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled && !active}
    className={`h-8 px-3 rounded-full border font-poppins text-[12px] font-medium transition-colors disabled:opacity-35 ${
      active ? 'text-white border-transparent' : 'border-black/20 text-black/80 hover:border-black'
    }`}
    style={active ? { backgroundColor: GREEN } : undefined}
  >
    {label}
  </button>
);

interface FilterSidebarProps {
  browse: ClothingBrowse;
}

const FilterSidebar: React.FC<FilterSidebarProps> = ({ browse }) => {
  const { filters, facets, brandFallback, toggle, setList, update } = browse;
  const [brandQuery, setBrandQuery] = useState('');
  const [showAllBrands, setShowAllBrands] = useState(false);

  // Types the facets know about that are not in any configured group
  const groups: TypeGroup[] = useMemo(() => {
    const known = new Set(TYPE_GROUPS.flatMap((g) => g.items));
    const extra = (facets?.productTypes || []).map((r) => r.value).filter((t) => !known.has(t));
    return extra.length ? [...TYPE_GROUPS, { id: 'other', name: 'Other', blurb: '', items: extra }] : TYPE_GROUPS;
  }, [facets]);

  const brandRows: FacetRow[] = useMemo(() => {
    const rows = facets?.brands || brandFallback.map((b) => ({ value: b, count: 0 }));
    const q = brandQuery.trim().toLowerCase();
    const filtered = q ? rows.filter((r) => r.value.toLowerCase().includes(q)) : rows;
    // Keep selected brands visible even when they have zero matches
    const selected = filters.brands.filter((b) => !filtered.some((r) => r.value === b)).map((b) => ({ value: b, count: 0 }));
    return [...selected, ...filtered];
  }, [facets, brandFallback, brandQuery, filters.brands]);

  const visibleBrands = showAllBrands || brandQuery ? brandRows : brandRows.slice(0, 10);

  const groupCount = (g: TypeGroup) =>
    facets ? g.items.reduce((sum, t) => sum + (countOf(facets.productTypes, t) || 0), 0) : undefined;

  const priceActive = (min?: number, max?: number) => filters.min === min && filters.max === max;

  return (
    <div className="font-poppins text-black">
      <ClothingSmartSearch browse={browse} />
      <Section title="Category" badge={filters.types.length}>
        <div className="space-y-1">
          {groups.map((g) => {
            const selectedInGroup = g.items.filter((t) => filters.types.includes(t));
            const open = selectedInGroup.length > 0;
            const total = groupCount(g);
            return (
              <details key={g.id} open={open} className="group/g rounded-lg">
                <summary className="flex items-center gap-2 py-1.5 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                  <ChevronDown className="w-3.5 h-3.5 text-black/40 -rotate-90 transition-transform group-open/g:rotate-0" />
                  <span className={`text-[13px] ${selectedInGroup.length ? 'font-semibold' : 'font-medium'}`}>{g.name}</span>
                  {selectedInGroup.length > 0 && (
                    <span className="text-[11px] font-semibold" style={{ color: GREEN }}>{selectedInGroup.length}</span>
                  )}
                  <Count n={total} />
                </summary>
                <div className="pl-5 pb-2">
                  <button
                    type="button"
                    onClick={() => setList('types', selectedInGroup.length === g.items.length ? [] : Array.from(new Set([...filters.types, ...g.items])))}
                    className="text-[11px] font-semibold uppercase tracking-[0.1em] hover:underline"
                    style={{ color: GREEN }}
                  >
                    {selectedInGroup.length === g.items.length ? 'Clear group' : `All ${g.name.toLowerCase()}`}
                  </button>
                  {g.items.map((t) => {
                    const n = countOf(facets?.productTypes, t);
                    if (facets && n === 0 && !filters.types.includes(t)) return null;
                    return <Check key={t} label={t} checked={filters.types.includes(t)} onChange={() => toggle('types', t)} count={n} />;
                  })}
                </div>
              </details>
            );
          })}
        </div>
      </Section>

      <Section title="Who is it for" badge={filters.genders.length + filters.ages.length}>
        <div className="flex flex-wrap gap-2">
          {['Male', 'Female', 'Unisex'].map((g) => (
            <Pill key={g} label={g === 'Male' ? 'Men' : g === 'Female' ? 'Women' : 'Unisex'} active={filters.genders.includes(g)} onClick={() => toggle('genders', g)} disabled={facets ? countOf(facets.genders, g) === 0 : false} />
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          {['Adult', 'Kids', 'Infant'].map((a) => (
            <Pill key={a} label={a} active={filters.ages.includes(a)} onClick={() => toggle('ages', a)} disabled={facets ? countOf(facets.ageGroups, a) === 0 : false} />
          ))}
        </div>
      </Section>

      <Section title="Colour" badge={filters.colours.length}>
        <div className="grid grid-cols-7 gap-2">
          {COLOUR_FAMILIES.map((c) => {
            const n = countOf(facets?.colourFamilies, c.name);
            const active = filters.colours.includes(c.name);
            const disabled = facets ? n === 0 : false;
            return (
              <button
                key={c.name}
                type="button"
                onClick={() => toggle('colours', c.name)}
                disabled={disabled && !active}
                title={n === undefined ? c.name : `${c.name} (${n})`}
                aria-label={c.name}
                aria-pressed={active}
                className={`relative aspect-square rounded-full border transition-transform disabled:opacity-25 hover:scale-105 ${
                  active ? 'ring-2 ring-offset-2 ring-[#78BE20] border-transparent' : 'border-black/15'
                }`}
                style={{ background: c.swatch }}
              />
            );
          })}
        </div>
        {filters.colours.length > 0 && (
          <p className="mt-2 text-[12px] text-black/60">{filters.colours.join(', ')}</p>
        )}
      </Section>

      <Section title="Brand" badge={filters.brands.length}>
        <div className="relative mb-2">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-black/40" />
          <input
            type="text"
            value={brandQuery}
            onChange={(e) => setBrandQuery(e.target.value)}
            placeholder="Find a brand"
            className="w-full h-9 pl-8 pr-3 rounded-md border border-black/15 bg-white text-[13px] outline-none focus:border-[#78BE20]"
          />
        </div>
        <div className="max-h-64 overflow-y-auto pr-1">
          {visibleBrands.map((r) => (
            <Check key={r.value} label={cleanBrand(r.value)} checked={filters.brands.includes(r.value)} onChange={() => toggle('brands', r.value)} count={facets ? r.count : undefined} />
          ))}
          {!brandRows.length && <p className="text-[12px] text-black/50">{brandQuery ? 'No brand matches.' : 'Loading brands…'}</p>}
        </div>
        {!showAllBrands && !brandQuery && brandRows.length > 10 && (
          <button type="button" onClick={() => setShowAllBrands(true)} className="mt-1 text-[12px] font-semibold hover:underline" style={{ color: GREEN }}>
            Show all {brandRows.length} brands
          </button>
        )}
      </Section>

      <Section title="Size" badge={filters.sizes.length} defaultOpen={false}>
        <div className="flex flex-wrap gap-1.5">
          {ADULT_SIZES.map((s) => (
            <Pill key={s} label={s} active={filters.sizes.includes(s)} onClick={() => toggle('sizes', s)} />
          ))}
        </div>
        <p className="mt-2 text-[11px] text-black/50">Kids’ sizes: filter by age group above.</p>
      </Section>

      <Section title="Price per garment" badge={filters.min !== undefined || filters.max !== undefined ? 1 : 0} defaultOpen={false}>
        <div className="flex flex-wrap gap-1.5">
          {PRICE_PRESETS.map((p) => (
            <Pill
              key={p.label}
              label={p.label}
              active={priceActive(p.min, p.max)}
              onClick={() => (priceActive(p.min, p.max) ? update({ min: undefined, max: undefined }) : update({ min: p.min, max: p.max }))}
            />
          ))}
        </div>
        <div className="flex items-center gap-2 mt-3">
          <input
            type="number"
            min={0}
            inputMode="decimal"
            placeholder="Min £"
            value={filters.min ?? ''}
            onChange={(e) => update({ min: e.target.value === '' ? undefined : Number(e.target.value) })}
            className="w-full h-9 px-3 rounded-md border border-black/15 text-[13px] outline-none focus:border-[#78BE20]"
          />
          <span className="text-black/40">–</span>
          <input
            type="number"
            min={0}
            inputMode="decimal"
            placeholder="Max £"
            value={filters.max ?? ''}
            onChange={(e) => update({ max: e.target.value === '' ? undefined : Number(e.target.value) })}
            className="w-full h-9 px-3 rounded-md border border-black/15 text-[13px] outline-none focus:border-[#78BE20]"
          />
        </div>
      </Section>

      <Section title="Sustainability" badge={filters.eco ? 1 : 0} defaultOpen={false}>
        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={filters.eco} onChange={() => update({ eco: !filters.eco })} className="peer sr-only" />
          <span className="relative w-10 h-6 rounded-full bg-black/20 transition-colors peer-checked:bg-[#78BE20] after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-5 after:h-5 after:rounded-full after:bg-white after:transition-transform peer-checked:after:translate-x-4" aria-hidden />
          <span className="flex items-center gap-1.5 text-[13px]">
            <Leaf className="w-4 h-4" style={{ color: GREEN }} />
            Organic or recycled only
            <Count n={facets?.sustainable} />
          </span>
        </label>
      </Section>
    </div>
  );
};

export default FilterSidebar;
